import type { UiState } from 'instantsearch.js';
import isEqual from 'lodash/isEqual';
import { useEffect, useRef } from 'react';
import { useInstantSearch } from 'react-instantsearch-core';

import { useRouter } from '@/hooks/useRouter';

type InstantSearchStateUpdaterProps = {
  expectedUiState: UiState;
};

/**
 * When we navigate to the same page from a next Link the search state is not updated so we need this
 * Or when navigating between 2 pages with same uiState, e.g. jump from `search/straps` to `straps/all` page
 *
 * Note: This should NOT run on initial SSR hydration - the InstantSearchSSRProvider
 * already provides the correct state. We only need this for client-side navigation.
 */
export const InstantSearchStateUpdater = ({
  expectedUiState,
}: InstantSearchStateUpdaterProps) => {
  const { setUiState, uiState: currentUiState } = useInstantSearch();
  const isInitialMount = useRef(true);
  const previousAsPath = useRef<string | null>(null);
  const router = useRouter();

  // Skip the initial mount - SSR already provides the correct state via InstantSearchSSRProvider
  // Only sync state on subsequent client-side navigations
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      previousAsPath.current = router.asPath;
      return;
    }

    // Only update if this is a real navigation (asPath changed) and states don't match
    if (
      previousAsPath.current !== router.asPath &&
      !isEqual(currentUiState, expectedUiState)
    ) {
      setUiState(expectedUiState);
    }
    previousAsPath.current = router.asPath;
  }, [currentUiState, expectedUiState, setUiState, router.asPath]);

  return null;
};
